import React from 'react';
import { ProductVariant } from '../../services/productService';

interface VariantSelectorProps {
  variants: ProductVariant[];
  selectedVariants: Record<string, string>; // variantType -> variantId
  onVariantChange: (variantType: string, variantId: string) => void;
  basePrice: number;
}

const VariantSelector: React.FC<VariantSelectorProps> = ({
  variants,
  selectedVariants,
  onVariantChange,
  basePrice,
}) => {
  if (!variants || variants.length === 0) {
    return null;
  }

  // Group variants by type
  const variantsByType: Record<string, ProductVariant[]> = {};
  variants.forEach((variant) => {
    if (variant.isAvailable) {
      if (!variantsByType[variant.variantType]) {
        variantsByType[variant.variantType] = [];
      }
      variantsByType[variant.variantType].push(variant);
    }
  });

  // Helper to get variant price display (basePrice + priceAdjustment)
  const getVariantPrice = (variant: ProductVariant): string | null => {
    const priceAdjustment = Number(variant.priceAdjustment) || 0;
    if (!isNaN(priceAdjustment) && priceAdjustment !== 0) {
      const finalPrice = Number(basePrice) + priceAdjustment;
      return `${finalPrice.toFixed(2)} د.ج`;
    }
    return null; // No adjustment, use base price
  };

  return (
    <div className="variant-selector space-y-4">
      {Object.entries(variantsByType).map(([type, typeVariants]) => {
        const selectedVariantId = selectedVariants[type];
        return (
          <div key={type}>
            <p className="text-[#0d171b] dark:text-slate-300 text-base font-medium leading-normal mb-2">
              {type}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {typeVariants.map((variant) => {
                const variantPrice = getVariantPrice(variant);
                return (
                  <button
                    key={variant.id}
                    onClick={() => onVariantChange(type, variant.id)}
                    className={`px-4 py-2 rounded-lg border font-semibold transition ${
                      selectedVariantId === variant.id
                        ? 'bg-primary/20 text-primary border-primary ring-2 ring-primary/30'
                        : 'bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <span>{variant.variantValue}</span>
                      {variantPrice && (
                        <span className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {variantPrice}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VariantSelector;
